/**
 * Dropdown Menu Renderer
 * @description 호버 시 서브메뉴가 표시되는 드롭다운 형태
 */

import { $$, TimeoutManager } from '../../../../core';
import type { Category, SideMenuElements } from '../types';
import type { SideMenuRenderer } from './types';

/**
 * 서브메뉴 아이템 렌더링 (재귀)
 */
const renderSubmenuItems = (items: Category[]): string => {
  return items
    .map((item) => {
      const hasChildren = item.children && item.children.length > 0;
      const childClass = hasChildren ? ' nde-c-side-menu__submenu-item--has-children' : '';

      const arrowIcon = hasChildren
        ? `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="nde-c-side-menu__submenu-arrow">
            <path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>`
        : '';

      const submenuHtml = hasChildren
        ? `<ul class="nde-c-side-menu__submenu">${renderSubmenuItems(item.children ?? [])}</ul>`
        : '';

      return `
        <li class="nde-c-side-menu__submenu-item${childClass}">
          <a href="${item.href}" class="nde-c-side-menu__submenu-link">
            <span>${item.label}</span>
            ${arrowIcon}
          </a>
          ${submenuHtml}
        </li>
      `;
    })
    .join('');
};

/**
 * 카테고리 렌더링
 */
const render = (list: HTMLElement, categories: Category[]): void => {
  list.innerHTML = categories
    .map((category) => {
      const hasChildren = category.children && category.children.length > 0;
      const activeClass = category.active ? ' nde-c-side-menu__link--active' : '';
      const targetAttr = category.isOpenNewTab ? ' target="_blank" rel="noopener noreferrer"' : '';

      const submenuHtml = hasChildren
        ? `<ul class="nde-c-side-menu__submenu">${renderSubmenuItems(category.children ?? [])}</ul>`
        : '';

      return `
        <li class="nde-c-side-menu__item">
          <a href="${category.href}"${targetAttr} class="nde-c-side-menu__link${activeClass}">
            ${category.label}
          </a>
          ${submenuHtml}
        </li>
      `;
    })
    .join('');
};

/**
 * 호버 인터랙션 설정
 */
const setupInteractions = ({ elements }: { elements: SideMenuElements; categories: Category[] }): void => {
  const { root } = elements;
  const items = $$<HTMLElement>('.nde-c-side-menu__item, .nde-c-side-menu__submenu-item--has-children', root);

  const closeTimeoutManager = new TimeoutManager<HTMLElement>();

  // 조상 요소들의 타임아웃 클리어
  const clearAncestorCloseTimeouts = (startItem: HTMLElement): void => {
    let current: HTMLElement | null = startItem;
    while (current && current !== document.body) {
      if (
        current.classList?.contains('nde-c-side-menu__item') ||
        current.classList?.contains('nde-c-side-menu__submenu-item')
      ) {
        closeTimeoutManager.clear(current);
      }
      current = current.parentElement;
    }
  };

  // 아이템 닫기
  const closeItem = (item: HTMLElement): void => {
    if (item.classList.contains('nde-c-side-menu__item')) {
      item.classList.remove('nde-c-side-menu__item--open');
    } else {
      item.classList.remove('nde-c-side-menu__submenu-item--open');
    }

    const submenu = item.querySelector<HTMLElement>(':scope > .nde-c-side-menu__submenu');
    if (submenu) {
      submenu.style.position = '';
      submenu.style.top = '';
      submenu.style.left = '';
      submenu.style.display = '';
    }

    closeTimeoutManager.clear(item);
  };

  const closeOtherOpenItems = (currentItem: HTMLElement): void => {
    const allOpenItems = $$<HTMLElement>('.nde-c-side-menu__item--open, .nde-c-side-menu__submenu-item--open');
    for (const openItem of allOpenItems) {
      if (openItem === currentItem || openItem.contains(currentItem)) {
        continue;
      }
      closeItem(openItem);
    }
  };

  const positionSubmenu = (submenu: HTMLElement, rect: DOMRect, isRootItem: boolean): void => {
    submenu.style.position = 'fixed';
    submenu.style.zIndex = '1100';
    submenu.style.display = 'block';

    if (isRootItem) {
      submenu.style.top = `${rect.bottom}px`;
      submenu.style.left = `${rect.left}px`;
    } else {
      submenu.style.top = `${rect.top - 8}px`;
      submenu.style.left = `${rect.right + 4}px`;
    }
  };

  const activateItem = (item: HTMLElement, isRootItem: boolean): void => {
    if (isRootItem) {
      item.classList.add('nde-c-side-menu__item--open');
    } else {
      item.classList.add('nde-c-side-menu__submenu-item--open');
    }
  };

  for (const item of items) {
    const submenu = item.querySelector<HTMLElement>(':scope > .nde-c-side-menu__submenu');
    if (!submenu) {
      continue;
    }

    // Mouse enter - 즉시 서브메뉴 표시
    item.addEventListener('mouseenter', () => {
      closeTimeoutManager.clear(item);
      clearAncestorCloseTimeouts(item);
      closeOtherOpenItems(item);

      const rect = item.getBoundingClientRect();
      const isRootItem = item.classList.contains('nde-c-side-menu__item');

      positionSubmenu(submenu, rect, isRootItem);
      activateItem(item, isRootItem);
    });

    // Mouse over - 조상 타임아웃 클리어
    item.addEventListener('mouseover', (e) => {
      e.stopPropagation();
      clearAncestorCloseTimeouts(item);
    });

    // Mouse leave - 딜레이 후 닫기
    item.addEventListener('mouseleave', (e) => {
      const relatedTarget = e.relatedTarget as Node | null;

      if (submenu.contains(relatedTarget) || item.contains(relatedTarget)) {
        return;
      }

      closeTimeoutManager.set(item, () => closeItem(item), 500);
    });

    // 서브메뉴 호버 시 열린 상태 유지
    submenu.addEventListener('mouseenter', () => {
      clearAncestorCloseTimeouts(item);
    });

    submenu.addEventListener('mouseleave', (e) => {
      const relatedTarget = e.relatedTarget as Node | null;

      if (item.contains(relatedTarget)) {
        return;
      }

      closeTimeoutManager.set(item, () => closeItem(item), 500);
    });
  }

  // 스크롤 시 모든 서브메뉴 닫기
  window.addEventListener(
    'scroll',
    () => {
      for (const item of items) {
        closeItem(item);
      }
    },
    { passive: true }
  );
};

export const dropdownRenderer: SideMenuRenderer = {
  render,
  setupInteractions,
};
